import {
  Component,
  JSXElement,
  Show,
  createEffect,
  onCleanup,
  onMount,
} from 'solid-js';
import { injectInRenderContext } from '../util';

const TEXT_INPUT_STYLES = `
  modus-wc-text-input input {
    font-weight: 400;
  }
  modus-wc-text-input input::placeholder {
    color: #9d9da6;
    font-style: italic;
    font-weight: 400;
  }
`;

export type TextInputType =
  | 'email'
  | 'password'
  | 'search'
  | 'tel'
  | 'text'
  | 'url';

export type TextInputSize = 'sm' | 'md' | 'lg';

export interface TextInputProps {
  value?: string;
  placeholder?: string;
  inputId?: string;
  name?: string;
  type?: TextInputType;
  size?: TextInputSize;
  bordered?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  /** Auto-focus the underlying input after the element mounts. */
  autoFocus?: boolean;
  /** Render the Modus built-in clear (x) affordance inside the input. */
  includeClear?: boolean;
  /** Render the Modus built-in search icon inside the input. */
  includeSearch?: boolean;
  customClass?: string;
  dataTheme?: string;
  label?: string;
  maxLength?: number;
  minLength?: number;
  inputTabIndex?: number;
  ariaLabel?: string;
  /**
   * Emits the raw input value whenever the underlying input changes.
   */
  onInputChange?(value: string): void;
  onInputBlur?(): void;
  onInputFocus?(): void;
  onClearClick?(): void;
}

/**
 * Reads the latest value out of a `modus-wc-text-input` `inputChange` event.
 * Modus puts the underlying `<input>` on `event.detail.target`.
 */
function readInputChangeValue(event: Event): string {
  const detail = (event as CustomEvent<unknown>).detail;
  if (detail && typeof detail === 'object' && 'target' in detail) {
    const target = (detail as { target?: HTMLInputElement | null }).target;
    return target?.value ?? '';
  }
  return '';
}

const TextInput: Component<TextInputProps> = (props): JSXElement => {
  let wrapperEl: HTMLDivElement | undefined;
  let hasAutoFocused = false;

  onMount(() => {
    let cancelled = false;
    void customElements.whenDefined('modus-wc-text-input').then(() => {
      if (cancelled || !wrapperEl) return;
      injectInRenderContext(
        wrapperEl,
        'mc-text-input-style',
        TEXT_INPUT_STYLES
      );
    });
    onCleanup(() => {
      cancelled = true;
    });
  });

  createEffect(() => {
    if (!props.autoFocus || !wrapperEl || hasAutoFocused) return;
    hasAutoFocused = true;

    const focusHost = (host: HTMLElement & { focus(): void }): void => {
      host.focus();
      if (document.activeElement !== host) {
        const innerInput =
          (host.shadowRoot?.querySelector(
            'input'
          ) as HTMLInputElement | null) ??
          (host.querySelector('input') as HTMLInputElement | null);
        innerInput?.focus();
      }
    };

    type ReadyHost = HTMLElement & {
      focus(): void;
      componentOnReady?(): Promise<unknown>;
    };

    void customElements
      .whenDefined('modus-wc-text-input')
      .then(() => {
        const host = wrapperEl?.querySelector(
          'modus-wc-text-input'
        ) as ReadyHost | null;
        if (!host) return undefined;
        const ready = host.componentOnReady?.();
        return Promise.resolve(ready).then(() => host);
      })
      .then((host) => {
        if (!host) return;
        requestAnimationFrame(() => focusHost(host));
      })
      .catch(() => {
        /* swallow — focus is best-effort, never block rendering */
      });
  });

  return (
    <div
      ref={wrapperEl}
      class="tc-modus-text-input-wrapper"
      attr:data-theme={props.dataTheme}
      style={{ display: 'contents' }}
    >
      <Show when={props.bordered === false}>
        <style>{`
          modus-wc-text-input label.modus-wc-input,
          modus-wc-text-input label.modus-wc-input:focus,
          modus-wc-text-input label.modus-wc-input:focus-within {
            outline: none !important;
            box-shadow: none !important;
            border: none !important;
          }
          modus-wc-text-input label.modus-wc-input input:focus,
          modus-wc-text-input label.modus-wc-input input:focus-visible {
            outline: none !important;
            box-shadow: none !important;
          }
        `}</style>
      </Show>
      <modus-wc-text-input
        value={props.value ?? ''}
        placeholder={props.placeholder}
        input-id={props.inputId}
        name={props.name}
        type={props.type ?? 'text'}
        size={props.size ?? 'md'}
        bordered={props.bordered ?? true}
        disabled={props.disabled}
        read-only={props.readOnly}
        required={props.required}
        include-clear={props.includeClear}
        include-search={props.includeSearch}
        custom-class={props.customClass}
        label={props.label}
        max-length={props.maxLength}
        min-length={props.minLength}
        input-tab-index={props.inputTabIndex}
        aria-label={props.ariaLabel}
        on:inputChange={(e) => props.onInputChange?.(readInputChangeValue(e))}
        on:inputBlur={() => props.onInputBlur?.()}
        on:inputFocus={() => props.onInputFocus?.()}
        on:clearClick={() => props.onClearClick?.()}
      />
    </div>
  );
};

export default TextInput;
